import React, { useEffect, useState } from "react";
import { getVariantLabel } from "@leafly-com/web-utils";
import classnames from "classnames";

import { getSimilarStrainMenuItems } from "api/requests/getSimilarStrainMenuItems";
import { MenuItem } from "custom-types/MenuItem";
import currencyFormatter, { centsToDollars } from "utils/currencyFormatter";
import fullVariantSizeText from "utils/getFullVariantSizeText";
import { getMenuItemUrl } from "utils/menuItemUrl";

import StarRating from "components/botanic/StarRating";
import EffectIcon from "components/Icons/EffectIcon";
import Logo from "components/Icons/logo.svg";
import LeaflyNug from "components/LeaflyNug";
import { AddToBagButton } from "components/Shared/MenuItemCarousel/AddToBagButton";

type Props = {
  menuItem: MenuItem;
  className?: string;
};

const ProductComparison: React.FC<Props> = ({ menuItem, className }) => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  const getNewMenuItems = async () => {
    if (menuItem.strain && menuItem.menuItemId) {
      const { menuItemMatches } = await getSimilarStrainMenuItems(
        menuItem.menuItemId
      );
      setMenuItems([menuItem, ...menuItemMatches]);
    }
  };

  useEffect(() => {
    getNewMenuItems();
  }, [menuItem.id]);

  if (!(menuItems.length > 1)) {
    return null;
  }

  return (
    <div className="bg-white border-t border-light-grey">
      <div className="container py-xxl">
        <div data-testid="category-carousel" className={classnames(className)}>
          <h2 className="mb-sm">Compare with similar items</h2>

          <div className="w-full overflow-x-auto lg:overflow-hidden">
            <table className="font-bold text-xs text-left">
              <tbody>
                <ComparisonRow>
                  {menuItems.map((menuItem, index) => (
                    <ComparisonCell
                      key={`${menuItem.menuItemId}-header`}
                      columnHeader
                    >
                      <div className="flex h-full flex-col justify-between">
                        {index === 0 ? (
                          <div className="flex flex-col items-center">
                            {menuItem.strain &&
                            // we are only showing logo image if item is in alberta, canada
                            menuItem?.dispensary?.state !== "AB" ? (
                              <LeaflyNug strain={menuItem.strain} />
                            ) : (
                              <span
                                aria-label="Leafly"
                                className="text-grey flex-shrink-0 header__logo"
                              >
                                <Logo width="50" height="80" />
                              </span>
                            )}

                            <div className="text-ellipsis">
                              This item: {menuItem.name}
                            </div>
                          </div>
                        ) : (
                          <a
                            href={getMenuItemUrl(menuItem)}
                            className="flex flex-col items-center"
                          >
                            {menuItem.strain &&
                            menuItem?.dispensary?.state !== "AB" ? (
                              <LeaflyNug strain={menuItem.strain} />
                            ) : (
                              <span
                                aria-label="Leafly"
                                className="text-grey flex-shrink-0 header__logo"
                              >
                                <Logo width="50" height="80" />
                              </span>
                            )}

                            <div
                              style={{ height: "40px" }}
                              className="underline overflow-hidden"
                            >
                              {menuItem.name}
                            </div>
                          </a>
                        )}

                        <AddToBagButton
                          defaultVariantId={menuItem.id}
                          menuItem={menuItem}
                        />
                      </div>
                    </ComparisonCell>
                  ))}
                </ComparisonRow>

                <ComparisonRow title="Amount" bottomBorder>
                  {menuItems.map((menuItem, index) => (
                    <ComparisonCell key={`${menuItem.menuItemId}-amount`}>
                      <>
                        {fullVariantSizeText(
                          getVariantLabel({
                            packageSize: menuItem.quantity,
                            packageUnit: menuItem.unit,
                          })
                        )}

                        {menuItem.variants &&
                          menuItem.variants.length > 1 &&
                          (index === 0 ? (
                            <span className="ml-md">+ more</span>
                          ) : (
                            <a
                              href={getMenuItemUrl(menuItem)}
                              className="button--text text-green ml-md"
                            >
                              + more
                            </a>
                          ))}
                      </>
                    </ComparisonCell>
                  ))}
                </ComparisonRow>

                <ComparisonRow title="Price" bottomBorder>
                  {menuItems.map((menuItem) => {
                    const { price, deal } = menuItem;

                    const displayPrice = deal?.active
                      ? centsToDollars(deal?.discountedPrice)
                      : price;

                    return (
                      <ComparisonCell key={`${menuItem.menuItemId}-price`}>
                        <div className="flex">
                          {deal && deal.active && (
                            <div className="text-white px-xs mr-sm bg-notification rounded">
                              {deal.discountLabel}
                            </div>
                          )}

                          {currencyFormatter(displayPrice, "US")}
                        </div>
                      </ComparisonCell>
                    );
                  })}
                </ComparisonRow>

                <ComparisonRow title="Cannabinoids" bottomBorder>
                  {menuItems.map((menuItem) => (
                    <ComparisonCell key={`${menuItem.menuItemId}-cannabinoids`}>
                      <>
                        <span className="mr-sm">
                          {`THC: ${
                            menuItem.strain?.cannabinoids?.thc.percentile50 ||
                            "<1"
                          }%`}
                        </span>
                        <span>
                          {`CBD: ${
                            menuItem.strain?.cannabinoids?.cbd.percentile50 ||
                            "<1"
                          }%`}
                        </span>
                      </>
                    </ComparisonCell>
                  ))}
                </ComparisonRow>

                <ComparisonRow title="Customer rating" bottomBorder>
                  {menuItems.map((menuItem) =>
                    menuItem.strain ? (
                      <ComparisonCell
                        key={`${menuItem.menuItemId}-customer-rating`}
                      >
                        <StarRating
                          rating={menuItem.strain?.averageRating}
                          ratingCount={menuItem.strain?.reviewCount}
                          starWidth={12}
                        />
                      </ComparisonCell>
                    ) : null
                  )}
                </ComparisonRow>

                <ComparisonRow title="Top reported effect" bottomBorder>
                  {menuItems.map((menuItem) => {
                    const topEffect = menuItem.strain?.topEffect;

                    if (!topEffect) {
                      return null;
                    }

                    return (
                      <ComparisonCell key={`${menuItem.menuItemId}-effect`}>
                        <div className="flex">
                          <EffectIcon effect={topEffect} className="mr-xs" />
                          {topEffect}
                        </div>
                      </ComparisonCell>
                    );
                  })}
                </ComparisonRow>

                <ComparisonRow title="Dominant terpene">
                  {menuItems.map((menuItem) => {
                    const terpName =
                      menuItem.strain?.topTerp &&
                      Object.values(menuItem.strain.topTerp)[0]?.name;

                    if (!terpName) {
                      return null;
                    }

                    return (
                      <ComparisonCell key={`${menuItem.menuItemId}-terpene`}>
                        <div className="flex items-center capitalize">
                          <div
                            className={`bg-${terpName} rounded-full ml-xs mr-sm`}
                            style={{ height: "20px", width: "12px" }}
                          />

                          {terpName}
                        </div>
                      </ComparisonCell>
                    );
                  })}
                </ComparisonRow>
              </tbody>
            </table>
          </div>
        </div>

        {/* In this table, after first row, first table cell */}
        <style jsx>{`
          tbody :global(tr:not(:first-child) td:first-of-type) {
            background-color: #e6f2f0;
          }
        `}</style>
      </div>
    </div>
  );
};

export default ProductComparison;

const HEADER_WIDTH = 145;
const HEADER_HEIGHT = 185;
const COLUMN_WIDTH = 167;

const ComparisonRow: React.FC<{
  title?: string;
  bottomBorder?: boolean;
  highlight?: boolean;
  children?: React.ReactNode;
}> = ({ title, bottomBorder = false, children }) => {
  const rowClasses = classnames("flex items-center", {
    "border-b border-light-grey": bottomBorder,
  });

  const headClasses = classnames("font-bold");

  return (
    <tr className={rowClasses}>
      {title ? (
        <th
          className={headClasses}
          style={{ width: HEADER_WIDTH }}
          scope="row"
          role="rowheader"
        >
          {title}
        </th>
      ) : (
        <td style={{ width: HEADER_WIDTH }}></td>
      )}
      {children}
    </tr>
  );
};

const ComparisonCell: React.FC<{
  columnHeader?: boolean;
  children?: React.ReactNode;
}> = ({ columnHeader = false, children }) => {
  return (
    <>
      {columnHeader ? (
        <th
          className="font-bold p-sm"
          style={{ height: HEADER_HEIGHT, width: COLUMN_WIDTH }}
          role="columnheader"
          scope="col"
        >
          {children}
        </th>
      ) : (
        <td className="p-sm" style={{ minHeight: 36, width: COLUMN_WIDTH }}>
          {children}
        </td>
      )}
    </>
  );
};
